// eslint-disable-next-line import/no-cycle
import { getScrollingManager } from '../../mobile-library/contentFrameInternals/getScrollingManager';
import { DocumentChunkManifest } from '../../types/chunkedDocuments';
import { isInReactNativeWebView } from '../../utils/environment';
import exceptionHandler from '../../utils/exceptionHandler.platform';
import makeLogger from '../../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import { portalGate as portalGateToForeground } from '../portalGates/contentFrame/from/reactNativeWebview';
import { ChunkContainerElement } from '../types/chunkedDocuments';
import { findChunkContainerForNode } from '../utils/findChunkContainerForNode';
import { resolveRelativePath } from '../utils/resolveRelativePath';

const logger = makeLogger(__filename);

type InlineableImageElement = HTMLImageElement | SVGImageElement;

export function prepareImageInlining(chunkContainerFragment: DocumentFragment, manifest: DocumentChunkManifest | undefined, chunkFilename: string) {
  if (!manifest) {
    exceptionHandler.captureException('Manifest is required for InlineImageObserver', { extra: { chunkFilename } });
    return;
  }

  const images = chunkContainerFragment.querySelectorAll<InlineableImageElement>('img, image');
  logger.debug(`Preparing and observing ${images.length} images in chunk ${chunkFilename}`);

  const relevantManifestEntries = Object.fromEntries(
    Object.values(manifest)
      .filter((m) => m?.filename)
      .map((m) => [m.filename, m]),
  );

  for (const imageElement of images) {
    let currentSrc = getImageSource(imageElement);

    const originalSrcAttr = imageElement.dataset.originalSrc;
    if (originalSrcAttr) {
      currentSrc = originalSrcAttr;
    }


    // If the src exists, isn't a data URL, and we haven't stored the original yet:
    // Store the intended source in data-originalSrc and clear the src/href
    // attribute to prevent the browser trying to load an invalid relative path immediately.
    if (currentSrc && !currentSrc.startsWith('data:') && !originalSrcAttr) {
      imageElement.dataset.originalSrc = currentSrc;
      if (imageElement.tagName.toLowerCase() === 'img') {
        imageElement.setAttribute('src', '');
      } else {
        imageElement.removeAttribute('href');
        imageElement.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
      }
    }

    const effectiveSrc = imageElement.dataset.originalSrc || currentSrc;
    if (effectiveSrc && !effectiveSrc.startsWith('data:')) {
      const absoluteFilename = resolveRelativePath(chunkFilename, effectiveSrc);
      const manifestEntry = relevantManifestEntries[absoluteFilename];

      imageElement.style.opacity = '0';
      imageElement.style.transition = 'opacity 0.1s ease-in-out';

      if (manifestEntry && manifestEntry.image_width && manifestEntry.image_height) {

        const width = manifestEntry.image_width.toString();
        const height = manifestEntry.image_height.toString();

        if (!imageElement.getAttribute('width')) {
          imageElement.setAttribute('width', width);
        }
        if (!imageElement.getAttribute('height')) {
          imageElement.setAttribute('height', height);
        }

        imageElement.style.width = width;
        imageElement.style.height = 'auto';
        imageElement.style.maxWidth = '100%';
        imageElement.style.aspectRatio = `${width} / ${height}`;
      }
    }
  }
}

/**
 * The `InlineImageObserver` is responsible for replacing the relative image URLs with actual base64 encoded images
 * for images within a specific chunk container.
 * It observes the images in the container and when they are in the viewport, it replaces the src with the base64 encoded image.
 * It also caches the base64 encoded images so that it doesn't have to process the same image twice within its own scope.
 *
 * This class should be instantiated once PER CHUNK CONTAINER when its content is loaded.
 */
export class InlineImageObserver {
  private observer: IntersectionObserver;

  private manifest: DocumentChunkManifest;
  private chunkContainerElement: ChunkContainerElement;
  private chunkFilename: string;
  private chunkId: string;
  private docId: string;
  // Map of original relative path src to data URL
  private processedImages: Map<string, string> = new Map();
  private documentTextContent: HTMLElement;

  constructor(
    chunkContainerElement: ChunkContainerElement,
    manifest: DocumentChunkManifest | undefined,
  ) {
    if (!manifest) {
      throw new Error('Manifest is required for InlineImageObserver');
    }
    if (!chunkContainerElement?.dataset?.chunkFilename) {
      throw new Error('Chunk container element must have a data-chunk-filename attribute');
    }
    const documentTextContent = document.getElementById('document-text-content');
    if (!documentTextContent) {
      throw new Error('Document text content element not found');
    }
    this.documentTextContent = documentTextContent;
    this.manifest = manifest;
    this.chunkContainerElement = chunkContainerElement;
    this.chunkFilename = chunkContainerElement.dataset.chunkFilename;
    this.chunkId = chunkContainerElement.dataset.chunkId;
    // Reads docId from the chunk container's data-doc-id attribute, set during chunk initialization.
    // Needed for Bookwise DRM: image chunk fetches require a per-doc license token.
    this.docId = chunkContainerElement.dataset.docId ?? '';


    // We preload images that are 2x the viewport height away from the current viewport.
    // This ensures that the user doesn't have to wait for images to load as they scroll.
    const verticalMargin = window.innerHeight * 2;

    this.observer = new IntersectionObserver(
      async (entries) => {
        const relevantTargets = entries
          .filter((entry) => {
            // SVG <image> elements often report false for isIntersecting only on Safari.
            // In this case, just check the intersection of the parent SVG instead.
            if (entry.target instanceof SVGImageElement && entry.target.parentElement instanceof SVGSVGElement) {
              const parentSvgRect = entry.target.parentElement.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              return parentSvgRect.top < viewportHeight && parentSvgRect.bottom > 0;
            }

            return entry.isIntersecting;
          })
          .map((entry) => entry.target)
          .filter((target): target is InlineableImageElement => {
            const isWithinChunkContainer = findChunkContainerForNode(target, this.documentTextContent) === this.chunkContainerElement;
            const isImage = target instanceof HTMLImageElement || target instanceof SVGImageElement;

            return isWithinChunkContainer && isImage;
          });
        logger.debug(`Loading ${relevantTargets.length} images for chunk ${this.chunkId}`, {
          relevantTargets,
        });

        if (relevantTargets.length <= 0) {
          return;
        }

        await this.loadObservedImages(relevantTargets);

        if (isInReactNativeWebView) {
          getScrollingManager().refreshPageSnapshotsForCurrentPage();
        }
      },
      {
        rootMargin: `${verticalMargin}px 0px`,
        threshold: 0.01,
      },
    );

    this.observeImages();
  }

  /** Disconnects the observer and clears the local cache for this chunk instance. */
  disconnect() {
    logger.debug(`Disconnecting InlineImageObserver for chunk ${this.chunkId} and clearing its cache.`);
    this.observer.disconnect();
    this.processedImages.clear();
  }

  private observeImages() {
    const images = this.chunkContainerElement.querySelectorAll<InlineableImageElement>('img, image');
    for (const imageElement of images) {
      this.observer.observe(imageElement);
    }
  }

  private async loadObservedImages(imageElements: InlineableImageElement[]) {
    const imagesRequiringLoad: {
      element: InlineableImageElement;
      originalSrc: string;
      absolutePath: string;
    }[] = [];

    for (const imageElement of imageElements) {
      const originalSrc = imageElement.dataset.originalSrc || getImageSource(imageElement) || '';

      if (!originalSrc) {
        continue;
      }

      const existingDataUrl = this.processedImages.get(originalSrc);

      if (existingDataUrl) {
        const currentAttrValue = getImageSource(imageElement);
        if (currentAttrValue !== existingDataUrl) {
          setImageSource(imageElement, existingDataUrl);
        }
        this.observer.unobserve(imageElement);
      } else if (
        originalSrc.startsWith('data:') ||
        originalSrc.startsWith('http://') ||
        originalSrc.startsWith('https://')
      ) {
        if (getImageSource(imageElement) !== originalSrc) {
          setImageSource(imageElement, originalSrc);
        }
        this.observer.unobserve(imageElement);
      } else {
        try {
          const absolutePath = resolveRelativePath(this.chunkFilename, originalSrc);
          imagesRequiringLoad.push({ element: imageElement, originalSrc, absolutePath });
        } catch (error) {
          exceptionHandler.captureException(error, { extra: { message: 'Error resolving image relative path', originalSrc, chunkFilename: this.chunkFilename } });
          restoreOriginalSource(imageElement, originalSrc);
          this.observer.unobserve(imageElement);
        }
      }
    }

    if (imagesRequiringLoad.length === 0) {
      return;
    }

    const absolutePathsToLoad = Array.from(new Set(imagesRequiringLoad.map((img) => img.absolutePath)));

    const overallStartTime = performance.now();
    let dataUrlsByAbsolutePath: { [absolutePath: string]: string; } = {};

    try {
      const dataUrls = await portalGateToForeground.methods.loadImagesAsDataUrlsFromManifest(
        this.manifest,
        absolutePathsToLoad,
        this.docId,
      );
      if (!dataUrls) {
        exceptionHandler.captureException('No data URLs returned from fetchAndProcessImageDataUrls', { extra: { chunkId: this.chunkId } });
        return;
      }
      dataUrlsByAbsolutePath = dataUrls;
      const fetchAndProcessTime = performance.now() - overallStartTime;
      logger.debug(`Fetched and processed ${Object.keys(dataUrlsByAbsolutePath).length} image data URLs in ${fetchAndProcessTime.toFixed(2)}ms.`);
    } catch (error) {
      exceptionHandler.captureException(error, { extra: { message: 'Error fetching/processing image data URLs in InlineImageObserver', chunkId: this.chunkId } });
      for (const { element, originalSrc } of imagesRequiringLoad) {
        if (element.isConnected) {
          restoreOriginalSource(element, originalSrc);
        }
        this.observer.unobserve(element);
      }
      return;
    }

    for (const { element, originalSrc, absolutePath } of imagesRequiringLoad) {
      if (!element.isConnected) {
        logger.warn('Image disconnected from DOM before data URL could be applied:', { originalSrc, chunkId: this.chunkId });
        this.observer.unobserve(element);
        continue;
      }

      const dataUrl = dataUrlsByAbsolutePath[absolutePath];

      if (dataUrl) {
        logger.debug(`Successfully retrieved data URL for ${originalSrc}. Size: ${dataUrl.length} bytes.`);
        this.processedImages.set(originalSrc, dataUrl);
        setImageSource(element, dataUrl);
      } else {
        logger.warn('Data URL not found for image after batch processing:', {
          originalSrc,
          absolutePath,
        });
        restoreOriginalSource(element, originalSrc);
      }
      this.observer.unobserve(element);
    }
  }
}


function getImageSource(imageElement: InlineableImageElement): string | null {
  if (imageElement.tagName.toLowerCase() === 'img') {
    return imageElement.getAttribute('src');
  } else {
    return imageElement.getAttribute('href') || imageElement.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
  }
}

function setImageSource(imageElement: InlineableImageElement, source: string) {
  if (imageElement.tagName.toLowerCase() === 'img') {
    imageElement.setAttribute('src', source);
  } else {
    imageElement.setAttribute('href', source);
    imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'href', source);
  }
  imageElement.style.opacity = '1';
}

function restoreOriginalSource(imageElement: InlineableImageElement, originalSrc: string) {
  logger.warn(`Restoring original source '${originalSrc}' for image element.`);
  setImageSource(imageElement, originalSrc);
}
